import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './Index.scss';
import { WaitlistContext } from '../../context/waitlistContext';
import { privacyUrl, termsUrl } from '../../Route_urls';

const Index = () => {
    const { email, setEmail } = useContext(WaitlistContext);
    const [stage, setStage] = useState('form');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [headlineIndex, setHeadlineIndex] = useState(0);
    const [qualification, setQualification] = useState({
        experienceLevel: '',
        tradeFrequency: '',
        currentTool: ''
    });

    const rotatingHeadlineWords = useMemo(() => [
        'clarity.',
        'focus.',
        'context.',
        'conviction.',
        'signal.',
        'insight.',
        'edge.',
        'direction.',
    ], []);

    const bullets = [
        {
            title: 'Know what deserves attention',
            text: 'See the business context, key drivers and signals behind every holding without digging through a dozen disconnected sources.'
        },
        {
            title: 'Make decisions with more conviction',
            text: 'Review valuation, growth, financial health and earnings quality in one focused workflow instead of piecing it together manually.'
        },
        {
            title: 'Spot stronger ideas sooner',
            text: 'Use smarter screening and portfolio-aware alerts to find opportunities earlier without drowning in generic market noise.'
        }
    ];

    const faqs = [
        {
            question: 'Who is this for?',
            answer: 'Self-directed investors who want to understand their holdings faster and make decisions without relying on noisy, fragmented tools.'
        },
        {
            question: 'What do waitlist members get?',
            answer: 'Early access, product updates, 50% off for life before launch pricing changes, and 5 free searches a week for stocks, funds and ETFs once you register.'
        },
        {
            question: 'Will it be hard to use?',
            answer: 'No. Investitum is built to make signals, drivers and portfolio changes obvious in plain English, not buried behind terminal-style complexity.'
        },
        {
            question: 'Is there a free version?',
            answer: 'Yes. Every registered user gets 5 free searches a week across stocks, funds and ETFs.'
        }
    ];

    function handleQualificationChange(event) {
        const { name, value } = event.target;
        setQualification((current) => ({
            ...current,
            [name]: value
        }));
    }

    const [displayedText, setDisplayedText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        let timer;
        const fullText = rotatingHeadlineWords[headlineIndex];
        const typingSpeed = 80;
        const deletingSpeed = 60;
        const pauseAfterTyping = 3500;

        if (!isDeleting && displayedText === fullText) {
            timer = setTimeout(() => setIsDeleting(true), pauseAfterTyping);
        } else if (isDeleting && displayedText === '') {
            setIsDeleting(false);
            setHeadlineIndex((i) => (i + 1) % rotatingHeadlineWords.length);
        } else {
            timer = setTimeout(() => {
                const nextText = isDeleting
                    ? fullText.slice(0, displayedText.length - 1)
                    : fullText.slice(0, displayedText.length + 1);
                setDisplayedText(nextText);
            }, isDeleting ? deletingSpeed : typingSpeed);
        }

        return () => clearTimeout(timer);
    }, [displayedText, isDeleting, headlineIndex, rotatingHeadlineWords]);

    async function handleWaitlistSubmit(e) {
        e.preventDefault();
        setError('');
        if (!email.trim()) return setError('Please enter a valid email');
        setLoading(true);
        try {
            const res = await fetch('/api/brevo', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: email.trim(),
                    action: 'subscribe',
                    experienceLevel: qualification.experienceLevel,
                    tradeFrequency: qualification.tradeFrequency,
                    currentTool: qualification.currentTool
                })
            });
            if (!res.ok) throw new Error('Failed to subscribe');
            setStage('done');
        } catch (err) {
            console.error(err);
            setError('Something went wrong — please try again.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="modern-landing">
            <div className="landing-shell">
                <section className="hero-layout">
                    <div className="hero-copy">
                        <div className="badge">Early access for self-directed investors</div>
                        <h1 className="hero-title">
                            <span className="headline-static">Most investors don’t lack data. They lack</span>
                            <div className="headline-rot-row">
                                <span className="headline-rotating">{displayedText}</span>
                            </div>
                        </h1>
                        <p className="hero-subtitle">
                            See what matters in your portfolio, what changed and what to focus on next without bouncing between noisy apps, headlines and spreadsheets.
                        </p>
                        <p className="promise-line">
                            A better way to invest with everything you need to understand your portfolio, minus the noise.
                        </p>

                        <div className="bullet-list" aria-label="Why join the waitlist">
                            {bullets.map((bullet) => (
                                <div key={bullet.title} className="bullet-item">
                                    <span className="bullet-mark" aria-hidden="true">+</span>
                                    <div>
                                        <strong>{bullet.title}</strong>
                                        <p>{bullet.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        
                    </div>

                    <div className="form-panel">
                        <div className="panel-eyebrow">One clear action</div>
                        <h2>Join investors choosing clarity over noise and lock in 50% off for life</h2>
                        <p className="panel-copy">
                            Get early access, secure launch pricing, and get 5 free searches a week across stocks, funds and ETFs once you register.
                        </p>

                        {stage === 'form' && (
                            <form onSubmit={handleWaitlistSubmit} className="join-form">
                                <label className="field-group">
                                    <span>Email</span>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        required
                                        className="waitlist-input"
                                        aria-label="Email address"
                                    />
                                </label>

                                <div className="field-grid">
                                    <label className="field-group">
                                        <span>Experience level</span>
                                        <select
                                            name="experienceLevel"
                                            value={qualification.experienceLevel}
                                            onChange={handleQualificationChange}
                                            className="waitlist-select"
                                            required
                                        >
                                            <option value="">Select one</option>
                                            <option value="Beginner">Beginner</option>
                                            <option value="Intermediate">Intermediate</option>
                                            <option value="Advanced">Advanced</option>
                                        </select>
                                    </label>

                                    <label className="field-group">
                                        <span>How often do you trade?</span>
                                        <select
                                            name="tradeFrequency"
                                            value={qualification.tradeFrequency}
                                            onChange={handleQualificationChange}
                                            className="waitlist-select"
                                            required
                                        >
                                            <option value="">Select one</option>
                                            <option value="A few times a year">A few times a year</option>
                                            <option value="Monthly">Monthly</option>
                                            <option value="Weekly or more">Weekly or more</option>
                                        </select>
                                    </label>
                                </div>

                                <label className="field-group">
                                    <span>What do you rely on most today?</span>
                                    <select
                                        name="currentTool"
                                        value={qualification.currentTool}
                                        onChange={handleQualificationChange}
                                        className="waitlist-select"
                                    >
                                        <option value="">Select one</option>
                                        <option value="Broker app">Broker app</option>
                                        <option value="Spreadsheets">Spreadsheets</option>
                                        <option value="Screeners and newsletters">Screeners and newsletters</option>
                                        <option value="A mix of everything">A mix of everything</option>
                                    </select>
                                </label>

                                <button type="submit" className="waitlist-submit" disabled={loading}>
                                    {loading ? 'Joining…' : 'Get Early Access and 50% Off for Life'}
                                </button>
                            </form>
                        )}

                        <div className="waitlist-trust">No spam. Just launch updates, early access, 5 free searches a week and the chance to lock in 50% off for life.</div>
                        {error && <div className="waitlist-error">{error}</div>}

                        {stage === 'done' && (
                            <div className="success-message">
                                <h3>You’re on the list.</h3>
                                <p>We’ll send early access details and the launch offer as soon as they are ready.</p>
                            </div>
                        )}
                    </div>
                </section>

                <section className="faq-strip" aria-label="FAQ">
                    {faqs.map((faq) => (
                        <article key={faq.question} className="faq-item">
                            <h3>{faq.question}</h3>
                            <p>{faq.answer}</p>
                        </article>
                    ))}
                </section>

                <footer className="landing-footer">
                    <p>
                        2026{' '}
                        <a
                            href="https://aviani.org"
                            target="_blank"
                            rel="noreferrer"
                            className="footer-link"
                        >
                            Aviani.org
                        </a>
                        . All rights reserved.
                    </p>
                    <nav className="footer-nav" aria-label="Footer links">
                        <Link to={privacyUrl} className="footer-link">Privacy</Link>
                        <span className="footer-divider" aria-hidden="true">/</span>
                        <Link to={termsUrl} className="footer-link">Terms</Link>
                    </nav>
                </footer>
            </div>
        </div>
    );
};

export default Index;